import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  useTriggerFormInit,
  useNodeDropdownLabels,
  buildShipmentFilterUpdate,
} from '../shared';
import { Form, Flex, message, Spin } from 'antd';
import useToken from '@/common/helper/useToken';
import { usePaginatedFilters } from '@/common/helper/usePaginatedFilters';
import { ALERT_TRIGGER_FILTER_CONFIGS } from '@/common/helper/filterConfigs';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import { getApplyConfig } from '@/pages/Settings/Notifications/NotificationConfig';
import { useFetchCompanyCouriers, useUnAuthorize } from '@/hooks';
import { getTeamsDDLOFFeature } from '@/common/utils';
import { type DropdownLabelItem } from '@/apis/logiblocksLabelsAPI';
import { ShipmentFilterApply } from '../alertTrigger/components';
import { CustomSelect, CustomShipmentText, SearchInput } from '@/components';
import { DownOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { LocationData } from '@/apis/types';
import { GetLocationsWithPaginationAndSearch } from '@/apis/onboarding';

import { ShipmentFilterFormData } from '../nodeFormTypes';

interface LocationTriggerFormData extends ShipmentFilterFormData {
  location?: string;
}

/**
 * Customization UI for LOCATION_TRIGGER nodes.
 */
export const LocationTriggerCustomization: CustomizationComponent = ({
  nodeData,
  onChange,
  isSidePannelOpen,
  nodeId,
  workflowId,
  isReadOnly = false,
}) => {
  const [form] = Form.useForm();
  const { getToken, token } = useToken();
  const { unauthorize } = useUnAuthorize();
  const { couriers } = useFetchCompanyCouriers();
  const [teamsList, setTeamsList] = useState([]);
  const [applyType, setApplyType] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState<boolean>(false);
  const dropdownScrollElRef = useRef<HTMLDivElement | null>(null);
  const [locationsList, setLocationsList] = useState<LocationData[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');

  // Pre-loaded labels for paginated dropdowns (fetched once when panel opens).
  const preloadedLabels = useNodeDropdownLabels({
    isSidePannelOpen,
    workflowId,
    nodeId,
    getToken,
  });

  const firstRunRef = useRef(true);

  // --- Locations fetch ---------------------------------------------------

  const loadLocations = useCallback(
    (pageNum: number, q: string, append = false) => {
      setDropdownLoading(true);
      GetLocationsWithPaginationAndSearch(token, false, pageNum, q)
        .then((res) => {
          const data = res?.data?.data;
          if (data?.is_success) {
            const incoming = data?.data?.data || [];
            setLocationsList((prev) =>
              append ? [...prev, ...incoming] : incoming
            );
            setTotalPages(data?.page_count);
            setPage(pageNum);
          } else {
            message.error(data?.message || 'Failed to fetch locations');
          }
        })
        .catch((error) => {
          unauthorize(error);
          message.error('Failed to fetch locations');
        })
        .finally(() => setDropdownLoading(false));
    },
    [token, unauthorize]
  );

  useEffect(() => {
    if (firstRunRef.current) {
      firstRunRef.current = false;
      loadLocations(1, search);
      return;
    }
    const t = setTimeout(() => loadLocations(1, search), 400);
    return () => clearTimeout(t);
  }, [search, loadLocations]);

  const fetchMore = useCallback(() => {
    if (dropdownLoading || page >= totalPages) return;
    loadLocations(page + 1, search, true);
  }, [dropdownLoading, page, totalPages, loadLocations, search]);

  // --- Pre-loaded dropdown labels fetched via useNodeDropdownLabels above ---

  // --- Teams -------------------------------------------------------------

  useEffect(() => {
    getTeamsDDLOFFeature('', 'Shipment').then((val) => {
      if (val && val.length > 0) setTeamsList(val);
    });
  }, []);

  // --- Paginated filters -------------------------------------------------

  const {
    getFilterData,
    getFilterLoading,
    getFilterHasMore,
    getFilterLoadMore,
  } = usePaginatedFilters(ALERT_TRIGGER_FILTER_CONFIGS, getToken);

  // --- Merge helper ------------------------------------------------------

  /**
   * Merge helper: prepend pre-loaded label items that are not already present
   * in the paginated page-1 data. Page data takes precedence (comes first so
   * the selector deduplication logic keeps the richer version).
   */
  const mergeWithPreloaded = useCallback(
    (pageData: unknown[], preloaded: DropdownLabelItem[]): unknown[] => {
      if (preloaded.length === 0) return pageData;
      const pageIds = new Set(
        (pageData as Array<{ id?: string | number }>).map((item) =>
          String(item.id ?? '')
        )
      );
      const extra = preloaded.filter((item) => !pageIds.has(String(item.id)));
      return [...pageData, ...extra];
    },
    []
  );

  // Memoized paginated data enriched with pre-loaded labels for off-page values.
  const memoizedOriginsData = useMemo(
    () => mergeWithPreloaded(getFilterData(3), preloadedLabels.origins),
    [getFilterData, mergeWithPreloaded, preloadedLabels.origins]
  );
  const memoizedProductsData = useMemo(
    () => mergeWithPreloaded(getFilterData(1), preloadedLabels.products),
    [getFilterData, mergeWithPreloaded, preloadedLabels.products]
  );
  const memoizedDestinationsData = useMemo(
    () => mergeWithPreloaded(getFilterData(4), preloadedLabels.destinations),
    [getFilterData, mergeWithPreloaded, preloadedLabels.destinations]
  );

  // Memoize location select options, combining paginated locationsList with pre-loaded labels.
  const memoizedLocationsOptions = useMemo(() => {
    const mainOptions = locationsList.map((loc) => ({
      value: loc.id,
      label: loc.name,
    }));
    const preloadedOptions = (preloadedLabels.location ?? []).map((item) => ({
      value: item.id,
      label: item.label,
    }));
    const mainIds = new Set(mainOptions.map((opt) => String(opt.value)));
    const extra = preloadedOptions.filter(
      (opt) => !mainIds.has(String(opt.value))
    );
    return [...mainOptions, ...extra];
  }, [locationsList, preloadedLabels.location]);

  const applyConfig = useMemo(
    () =>
      getApplyConfig({
        teamsList,
        origins: memoizedOriginsData,
        products: memoizedProductsData,
        couriers,
        destinations: memoizedDestinationsData,
      }),
    [
      teamsList,
      memoizedOriginsData,
      memoizedProductsData,
      couriers,
      memoizedDestinationsData,
    ]
  );

  // --- Node update handlers ----------------------------------------------

  const handleNodeData = (
    data: LocationTriggerFormData,
    isSelectAll?: boolean
  ) => {
    if (!data || Object.keys(data).length === 0) return;
    onChange({
      location_trigger: buildShipmentFilterUpdate(
        data,
        nodeData.location_trigger,
        applyType,
        undefined,
        isSelectAll
      ),
    });
  };

  const handleLocationChange = useCallback(
    (value: string) => {
      onChange({
        location_trigger: {
          ...nodeData.location_trigger,
          location: value,
        },
      });
    },
    [nodeData.location_trigger, onChange]
  );

  // --- Form initialization -----------------------------------------------

  const hasPatchedRef = useTriggerFormInit({
    isSidePannelOpen,
    triggerData: nodeData?.location_trigger,
    form,
    setApplyType,
    extraFormValues: {
      location: nodeData?.location_trigger?.location ?? undefined,
    },
  });

  // Reset the form-init patch flag on panel close so re-opening re-initializes.
  useEffect(() => {
    if (!isSidePannelOpen) {
      hasPatchedRef.current = false;
    }
  }, [isSidePannelOpen, hasPatchedRef]);

  // --- Scroll handler ----------------------------------------------------

  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      if (!target) return;
      const nearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
      if (nearBottom) fetchMore();
    },
    [fetchMore]
  );

  return (
    <Form form={form} layout='vertical'>
      <Flex vertical gap={16}>
        <Flex vertical gap={4}>
          <CustomShipmentText
            text={'Shipment arriving at location'}
            fontSize='14px'
            fontWeight={600}
            marginBottom={'4px'}
          />
          <Form.Item
            className='custom-input form-input-style'
            name='location'
            rules={[
              {
                required: true,
                message: 'Please select a shipment arrival location',
              },
            ]}
          >
            <CustomSelect
              width='100%'
              placeholder='Select...'
              suffixIcon={
                <DownOutlined style={{ color: 'var(--text-secondary)' }} />
              }
              options={memoizedLocationsOptions}
              bgColor='var(--light-gray)'
              textColor='var(--text-secondary)'
              borderRadius='8px'
              height='36px'
              border='none'
              placeholderColor='var(--text-secondary)'
              filterOption={false}
              onChange={handleLocationChange}
              disabled={isReadOnly}
              onPopupScroll={handlePopupScroll}
              notFoundContent={
                dropdownLoading ? null : (
                  <div style={{ padding: 8 }}>No locations found</div>
                )
              }
              dropdownRender={(menu: React.ReactNode) => (
                <>
                  <div
                    style={{
                      padding: '6px 16px 12px',
                      borderBottom: '1px solid var(--dark-border)',
                    }}
                  >
                    <SearchInput
                      placeholder='Search location'
                      width='100%'
                      onChange={(val) => setSearch(val)}
                      onClear={() => setSearch('')}
                    />
                  </div>
                  <div
                    ref={dropdownScrollElRef}
                    style={{ maxHeight: 256, overflow: 'auto' }}
                  >
                    {menu}
                  </div>
                  {dropdownLoading && (
                    <div
                      style={{
                        padding: 8,
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <Spin size='small' />
                    </div>
                  )}
                </>
              )}
            />
          </Form.Item>
          <p
            className='font-12 fw-400 lh-20'
            style={{ color: 'var(--text-secondary)' }}
          >
            Not in the list? Create one{' '}
            <Link
              className='black-link'
              to='/settings/location'
              state={{ openAddModal: true }}
            >
              here
            </Link>
          </p>
        </Flex>
        <ShipmentFilterApply
          applyType={applyType}
          setApplyType={setApplyType}
          form={form}
          applyConfig={applyConfig}
          handleNodeUpdate={handleNodeData}
          isReadOnly={isReadOnly}
          origins={memoizedOriginsData}
          originsLoading={getFilterLoading(3)}
          originsHasMore={getFilterHasMore(3)}
          onLoadMoreOrigins={getFilterLoadMore(3)}
          products={memoizedProductsData}
          productsLoading={getFilterLoading(1)}
          productsHasMore={getFilterHasMore(1)}
          onLoadMoreProducts={getFilterLoadMore(1)}
          destinations={memoizedDestinationsData}
          destinationsLoading={getFilterLoading(4)}
          destinationsHasMore={getFilterHasMore(4)}
          onLoadMoreDestinations={getFilterLoadMore(4)}
        />
      </Flex>
    </Form>
  );
};
